import { functionsAxiosInstance } from "../../api"
import type { OauthStateParam } from "../../auth/api"
import {
  ATLASSIAN_PROVIDER,
  GONG_PROVIDER,
  GOOGLE_PROVIDER,
  LINEAR_PROVIDER,
  NOTION_PROVIDER,
  SALESFORCE_PROVIDER,
  ZOOM_PROVIDER,
} from "../../credentials/providerTypes"
import { openUrlInPopup } from "../../windowUtils"

interface OAuthProviderInfo {
  baseUrl: string
  redirectSuffix: string
  client_id?: string
  extraParams?: Record<string, string>
  requiresPkce?: boolean
}

const OAUTH_PROVIDER_MAPPING: Record<string, OAuthProviderInfo> = {
  [GOOGLE_PROVIDER]: {
    baseUrl: "https://accounts.google.com/o/oauth2/v2/auth",
    redirectSuffix: "/v1/googleAuthCallback",
    client_id: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID,
    extraParams: {
      include_granted_scopes: "true",
      enable_granular_consent: "true",
      // Required to get refresh token (I think).
      prompt: "consent",
      access_type: "offline",
    },
  },
  [GONG_PROVIDER]: {
    baseUrl: "https://app.gong.io/oauth2/authorize",
    redirectSuffix: "/v1/gongAuthCallback",
    client_id: import.meta.env.VITE_GONG_OAUTH_CLIENT_ID,
  },
  [SALESFORCE_PROVIDER]: {
    baseUrl: "https://login.salesforce.com/services/oauth2/authorize",
    redirectSuffix: "/v1/salesforceAuthCallback",
    client_id: import.meta.env.VITE_SALESFORCE_OAUTH_CLIENT_ID,
    requiresPkce: true,
  },
  [ZOOM_PROVIDER]: {
    baseUrl: "https://zoom.us/oauth/authorize",
    redirectSuffix: "/v1/zoomAuthCallback",
    client_id: import.meta.env.VITE_ZOOM_OAUTH_CLIENT_ID,
  },
  [NOTION_PROVIDER]: {
    baseUrl: "https://api.notion.com/v1/oauth/authorize",
    redirectSuffix: "/v1/notionAuthCallback",
    client_id: import.meta.env.VITE_NOTION_OAUTH_CLIENT_ID,
  },
  [ATLASSIAN_PROVIDER]: {
    baseUrl: "https://auth.atlassian.com/authorize",
    redirectSuffix: "/v1/atlassianAuthCallback",
    client_id: import.meta.env.VITE_ATLASSIAN_OAUTH_CLIENT_ID,
    // Required params for Atlassian only
    extraParams: {
      audience: "api.atlassian.com",
      scope:
        "read:page:confluence+read:confluence-content.all+read:confluence-content.permission+read:confluence-content.summary+read:confluence-groups+read:confluence-props+read:confluence-space.summary+read:confluence-user+readonly:content.attachment:confluence+search:confluence+offline_access",
      prompt: "consent",
    },
  },
  [LINEAR_PROVIDER]: {
    baseUrl: "https://linear.app/oauth/authorize",
    redirectSuffix: "/v1/linearAuthCallback",
    client_id: import.meta.env.VITE_LINEAR_OAUTH_CLIENT_ID,
    extraParams: {
      scope: "comments:create",
      actor: "application",
    },
  },
}

const getOauthUrl = (
  provider: keyof typeof OAUTH_PROVIDER_MAPPING,
  stateParam: OauthStateParam,
  extraParams: Record<string, string>,
): string => {
  const { baseUrl, redirectSuffix, client_id, requiresPkce } =
    OAUTH_PROVIDER_MAPPING[provider]

  if (!client_id) {
    throw new Error(`Missing ${provider} client id`)
  }

  const redirectUri = `${functionsAxiosInstance.defaults.baseURL}${redirectSuffix}`
  const params = new URLSearchParams({
    client_id,
    response_type: "code",
    owner: "user",
    redirect_uri: redirectUri,
    state: stateParam.state,
    ...(stateParam.code_challenge && requiresPkce
      ? {
          code_challenge_method: stateParam.code_challenge_method,
          code_challenge: stateParam.code_challenge,
        }
      : {}),
    ...(OAUTH_PROVIDER_MAPPING[provider].extraParams ?? {}),
    ...extraParams,
  })

  return `${baseUrl}?${params.toString()}`
}

export type OAUTH_PROVIDERS = keyof typeof OAUTH_PROVIDER_MAPPING

export async function doOauthFlow(
  provider: OAUTH_PROVIDERS,
  stateParam: OauthStateParam,
  extraParams: Record<string, string>,
): Promise<void> {
  const url = getOauthUrl(provider, stateParam, extraParams)
  await openUrlInPopup(
    url,
    `${provider[0].toUpperCase()}${provider.slice(1)} OAuth`,
  )
}
